import { useApiToast } from 'components/Toaster';
import { getDatalists } from 'features/datalist';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetPersonMutation, useUpdatePersonMutation } from 'services/person/person.endpoint';
import { Person, UpdatePersonPayload } from 'services/person/person.types';
import { useGetAllUsersMutation } from 'services/user/user.endpoint';
import { User } from 'types/User';

export default function usePersonDetailsForm() {
    const { personId } = useParams();

    const [person, setPerson] = useState<Person | null>(null);
    const [users, setUsers] = useState<Array<User>>([]);
    const datalists = useSelector(getDatalists);
    const navigate = useNavigate();

    const [
        getPerson,
        { isLoading: isGetPersonLoading, isSuccess: isGetPersonSuccess, isUninitialized: isGetPersonUninitialized, data: getPersonResponse }
    ] = useGetPersonMutation();

    useEffect(() => {
        if (!personId) return;
        getPerson({ id: personId });
    }, [getPerson, personId]);

    useEffect(() => {
        getAllUsers(undefined);
    }, []);

    useEffect(() => {
        if (isGetPersonSuccess && getPersonResponse) {
            setPerson(getPersonResponse.data);
        }
    }, [isGetPersonSuccess, getPersonResponse]);

    const [
        updatePerson,
        {
            isLoading: isUpdatePersonLoading,
            isError: isUpdatePersonError,
            isSuccess: isUpdatePersonSuccess,
            error: updatePersonError,
            data: updatePersonResponse
        }
    ] = useUpdatePersonMutation();

    const [
        getAllUsers,
        {
            isLoading: isGetAllUsersLoading,
            isError: isGetAllUsersError,
            isSuccess: isGetAllUsersSuccess,
            error: getAllUsersError,
            data: getAllUsersData
        }
    ] = useGetAllUsersMutation();

    const { showErrorToast, showApiToast } = useApiToast();

    useEffect(() => {
        if (!person) return;
        if (isUpdatePersonError) {
            showErrorToast(updatePersonError, {
                message: `Erreur lors de la mise à jour des informations de "${person.lastname} ${person.firstname}"`
            });
        }

        if (isUpdatePersonSuccess && updatePersonResponse) {
            showApiToast(updatePersonResponse, {
                successMessage: `Informations de "${person.lastname} ${person.firstname}" mises à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour des informations de "${person.lastname} ${person.firstname}"`
            });

            navigate(`/app/admin/persons/${person.id}`);
        }
    }, [
        showErrorToast,
        showApiToast,
        isUpdatePersonError,
        isUpdatePersonSuccess,
        updatePersonError,
        updatePersonResponse,
        person,
        navigate
    ]);

    useEffect(() => {
        if (isGetAllUsersLoading) return;
        if (isGetAllUsersError && getAllUsersError) {
            showErrorToast(getAllUsersError, {
                message: 'Erreur lors de la récupération des utilisateurs'
            });
            return;
        }
        if (isGetAllUsersSuccess && getAllUsersData) {
            setUsers(getAllUsersData.data);
            return;
        }
    }, [isGetAllUsersLoading, isGetAllUsersError, isGetAllUsersSuccess, getAllUsersError, getAllUsersData]);

    async function handleUpdatePersonInfo(data: UpdatePersonPayload['body']) {
        if (!person) return;
        await updatePerson({
            id: person?.id,
            body: data
        });
    }

    return {
        person,
        isGetPersonUninitialized,
        isGetPersonLoading,
        isLoading: isGetPersonLoading || isUpdatePersonLoading,
        datalists,
        handleUpdatePersonInfo,
        users
    };
}
